"use client";

import { type ReactNode, useCallback, useState } from "react";
import { Checkbox, Panel, TextField } from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import type { SortOrder } from "@/lib/tool-kit/charts/bars";
import {
  CHART_COLOR_IDS,
  type ChartColorId,
} from "@/lib/tool-kit/charts/color";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/**
 * The controls every chart shares: titles, colour, value labels and APA mode.
 *
 * One state object rather than eight `useState`s, so a widget passes `style` and
 * `update` and the kit draws the fields. Which fields appear is the widget's choice —
 * a pie chart has no axes, a stem-and-leaf plot no colour.
 */

export interface ChartStyle {
  title: string;
  xTitle: string;
  yTitle: string;
  color: ChartColorId;
  valueLabels: boolean;
  apa: boolean;
  figureNumber: string;
  note: string;
}

export function useChartStyle(initial: ChartStyle) {
  const [style, setStyle] = useState(initial);
  const update = useCallback(
    (patch: Partial<ChartStyle>) => setStyle((old) => ({ ...old, ...patch })),
    []
  );
  return [style, update] as const;
}

/** Names for the eight palette colours, in `CHART_COLOR_IDS` order. */
export interface ChartColorLabels {
  colorLabel: string;
  colorBlue: string;
  colorOrange: string;
  colorGreen: string;
  colorVermillion: string;
  colorSkyBlue: string;
  colorPurple: string;
  colorYellow: string;
  colorBlack: string;
}

const COLOR_LABEL_KEYS: Record<ChartColorId, keyof ChartColorLabels> = {
  blue: "colorBlue",
  orange: "colorOrange",
  green: "colorGreen",
  vermillion: "colorVermillion",
  skyBlue: "colorSkyBlue",
  purple: "colorPurple",
  yellow: "colorYellow",
  black: "colorBlack",
};

export function colorName(id: ChartColorId, labels: ChartColorLabels): string {
  return labels[COLOR_LABEL_KEYS[id]];
}

/** A row of colour swatches — radio buttons named for screen readers. */
export function ColorSwatches({
  name,
  value,
  onChange,
  labels,
  legend,
}: {
  /** Radio group name, unique on the page. */
  name: string;
  value: ChartColorId;
  onChange: (value: ChartColorId) => void;
  labels: ChartColorLabels;
  /** Overrides `labels.colorLabel`, e.g. "First series color". */
  legend?: string;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className={LABEL}>{legend ?? labels.colorLabel}</legend>
      <div className="flex flex-wrap gap-2">
        {CHART_COLOR_IDS.map((id, index) => {
          const label = colorName(id, labels);
          return (
            <label className="cursor-pointer" key={id} title={label}>
              <input
                checked={value === id}
                className="peer sr-only"
                name={name}
                onChange={() => onChange(id)}
                type="radio"
                value={id}
              />
              <span
                aria-hidden="true"
                className="block h-8 w-8 rounded-full border border-border ring-offset-2 ring-offset-card peer-checked:ring-2 peer-checked:ring-foreground peer-focus-visible:ring-2 peer-focus-visible:ring-primary"
                style={{ backgroundColor: CHART_COLORS[index] }}
              />
              <span className="sr-only">{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface ChartStyleLabels {
  labelsAndStyle: string;
  chartTitle: string;
  apaStyle: string;
  apaHint: string;
  figureNumber: string;
  figureNote: string;
}

/** Axis-title field labels. Without the y keys, only the x field is drawn. */
export interface AxisTitleLabels {
  xAxisTitle: string;
  axisTitlePlaceholderX: string;
  yAxisTitle?: string;
  axisTitlePlaceholderY?: string;
}

/**
 * The "Labels and style" panel. A field appears when its labels are passed: axis
 * titles with `axisLabels`, colour swatches with `colorLabels`, the value-label switch
 * with `valueLabel`. `children` adds chart-specific options (sort order, markers, a
 * trend line) inside the same panel.
 */
export function ChartStyleFields({
  idPrefix,
  style,
  update,
  labels,
  axisLabels,
  colorLabels,
  valueLabel,
  children,
}: {
  idPrefix: string;
  style: ChartStyle;
  update: (patch: Partial<ChartStyle>) => void;
  labels: ChartStyleLabels;
  axisLabels?: AxisTitleLabels;
  colorLabels?: ChartColorLabels;
  valueLabel?: string;
  children?: ReactNode;
}) {
  return (
    <Panel title={labels.labelsAndStyle}>
      <TextField
        id={`${idPrefix}-title`}
        label={labels.chartTitle}
        onChange={(title) => update({ title })}
        value={style.title}
      />
      {axisLabels ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id={`${idPrefix}-x-title`}
            label={axisLabels.xAxisTitle}
            onChange={(xTitle) => update({ xTitle })}
            placeholder={axisLabels.axisTitlePlaceholderX}
            value={style.xTitle}
          />
          {axisLabels.yAxisTitle ? (
            <TextField
              id={`${idPrefix}-y-title`}
              label={axisLabels.yAxisTitle}
              onChange={(yTitle) => update({ yTitle })}
              placeholder={axisLabels.axisTitlePlaceholderY}
              value={style.yTitle}
            />
          ) : null}
        </div>
      ) : null}
      {colorLabels ? (
        <ColorSwatches
          labels={colorLabels}
          name={`${idPrefix}-color`}
          onChange={(value) => update({ color: value })}
          value={style.color}
        />
      ) : null}
      {children}
      {valueLabel ? (
        <Checkbox
          checked={style.valueLabels}
          label={valueLabel}
          onChange={(checked) => update({ valueLabels: checked })}
        />
      ) : null}
      <div className="space-y-2">
        <Checkbox
          checked={style.apa}
          label={labels.apaStyle}
          onChange={(apa) => update({ apa })}
        />
        <p className={HINT}>{labels.apaHint}</p>
      </div>
      {style.apa ? (
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          <TextField
            id={`${idPrefix}-figure-number`}
            label={labels.figureNumber}
            numeric
            onChange={(figureNumber) => update({ figureNumber })}
            value={style.figureNumber}
          />
          <TextField
            id={`${idPrefix}-figure-note`}
            label={labels.figureNote}
            onChange={(note) => update({ note })}
            value={style.note}
          />
        </div>
      ) : null}
    </Panel>
  );
}

export interface SortLabels {
  sortLabel: string;
  sortNone: string;
  sortAscending: string;
  sortDescending: string;
}

export function sortOptions(
  labels: SortLabels
): { value: SortOrder; label: string }[] {
  return [
    { value: "none", label: labels.sortNone },
    { value: "ascending", label: labels.sortAscending },
    { value: "descending", label: labels.sortDescending },
  ];
}

export interface AxisRangeLabels {
  rangesLabel: string;
  xMin: string;
  xMax: string;
  yMin: string;
  yMax: string;
  rangeAuto: string;
}

export interface AxisBounds {
  xMin: string;
  xMax: string;
  yMin: string;
  yMax: string;
}

/**
 * Four optional fields that pin the ends of the x and y axes. Values stay text so a
 * half-typed "-" or "2," does not jump; the chart parses them with `parseNumberInput`.
 */
export function AxisRangeFields({
  idPrefix,
  bounds,
  onChange,
  labels,
}: {
  idPrefix: string;
  bounds: AxisBounds;
  onChange: (bounds: AxisBounds) => void;
  labels: AxisRangeLabels;
}) {
  const keys = ["xMin", "xMax", "yMin", "yMax"] as const;
  return (
    <fieldset className="space-y-3">
      <legend className={LABEL}>{labels.rangesLabel}</legend>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {keys.map((key) => (
          <TextField
            id={`${idPrefix}-${key}`}
            key={key}
            label={labels[key]}
            numeric
            onChange={(value) => onChange({ ...bounds, [key]: value })}
            placeholder={labels.rangeAuto}
            value={bounds[key]}
          />
        ))}
      </div>
    </fieldset>
  );
}
